import React from 'react';
import { useLingui } from '@lingui/react/macro';

import CarouselLeftSmall from '@rover/icons/svg/carousel-left-small.svg';
import CarouselRightSmall from '@rover/icons/svg/carousel-right-small.svg';
import { Flex } from '@rover/kibble/core';
import Button from '@rover/kibble/core/Button';

export enum PaginateDirection {
  NEXT = 1,
  PREVIOUS = -1,
}

export enum StepperVariant {
  DEFAULT = 'default',
  SMALL = 'small',
}

type Props = React.ComponentProps<typeof Flex> & {
  currentPage: number;
  totalPages: number;
  canPaginateNext: boolean;
  canPaginatePrevious: boolean;
  hideNumbers?: boolean;
  stepperVariant?: StepperVariant;
  handlePageChange: (direction: PaginateDirection) => void;
};

const Stepper: React.FC<Props> = ({
  handlePageChange,
  currentPage,
  totalPages,
  canPaginateNext,
  canPaginatePrevious,
  hideNumbers = false,
  stepperVariant = StepperVariant.DEFAULT,
  ...flexProps
}) => {
  const { t } = useLingui();
  const isSmall = stepperVariant === StepperVariant.SMALL;

  return (
    <Flex
      minWidth="15x"
      gap={isSmall ? '2x' : '5x'}
      flex="none"
      alignItems="center"
      justifyContent={['center', 'right']}
      {...flexProps}
    >
      <Button
        data-qa-id="paginate-previous"
        data-testid="paginate-previous"
        icon={CarouselLeftSmall}
        size={isSmall ? 'small' : 'default'}
        disabled={!canPaginatePrevious}
        circular
        aria-label={t`paginate previous`}
        onClick={() => handlePageChange(PaginateDirection.PREVIOUS)}
      />
      {!hideNumbers && `${currentPage} / ${totalPages}`}
      <Button
        data-qa-id="paginate-next"
        data-testid="paginate-next"
        icon={CarouselRightSmall}
        size={isSmall ? 'small' : 'default'}
        disabled={!canPaginateNext}
        circular
        aria-label={t`paginate next`}
        onClick={() => handlePageChange(PaginateDirection.NEXT)}
      />
    </Flex>
  );
};

export default Stepper;
